import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from '../../components/Portal/PortalLayout';
import { memberAPI } from '../../services/api';
import { cn } from '../../lib/utils';

/**
 * CommunicationSettings - Manage notification preferences
 * Email, SMS, marketing, and tournament reminders
 */
const CommunicationSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: true,
    tournamentReminders: true,
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const memberId = user.memberId;

  useEffect(() => {
    const fetchSettings = async () => {
      if (!memberId) {
        setError('No member ID found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const response = await memberAPI.getPortalSummary(memberId);
        const member = response.data.data.member;
        const prefs = member.communicationPreferences || {};

        setFormData({
          emailNotifications: prefs.emailNotifications ?? true,
          smsNotifications: prefs.smsNotifications ?? false,
          marketingEmails: prefs.marketingEmails ?? true,
          tournamentReminders: prefs.tournamentReminders ?? true,
        });
      } catch (err) {
        console.error('Failed to load settings:', err);
        setError('Failed to load your settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [memberId]);

  const handleToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await memberAPI.updateCommunication(memberId, formData);
      setSuccess('Settings saved successfully!');
      setTimeout(() => {
        navigate('/portal');
      }, 1500);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleOptions = [
    {
      field: 'emailNotifications',
      label: 'Email Notifications',
      description: 'Receive tournament updates, tee sheet assignments, and important announcements via email',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      field: 'smsNotifications',
      label: 'SMS Notifications',
      description: 'Get text messages for urgent updates like schedule changes or weather alerts',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      field: 'tournamentReminders',
      label: 'Tournament Reminders',
      description: 'Get reminded about upcoming tournaments, registration deadlines, and tee times',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      field: 'marketingEmails',
      label: 'Club News & Updates',
      description: 'Receive newsletters, club news, and promotional information',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
    },
  ];

  if (loading) {
    return (
      <PortalLayout title="Alerts" showBack>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="Alerts" showBack>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-blue-800">
              Control how and when you receive notifications from the club. We recommend keeping tournament reminders enabled.
            </p>
          </div>
        </div>

        {/* Toggle Options */}
        <div className="space-y-3">
          {toggleOptions.map((option) => (
            <div
              key={option.field}
              className="bg-white rounded-xl p-4 border border-gray-200"
            >
              <div className="flex items-start gap-4">
                <div className="text-gray-400 mt-1">
                  {option.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-medium text-gray-900">{option.label}</span>
                    <button
                      type="button"
                      onClick={() => handleToggle(option.field)}
                      className={cn(
                        'relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                        formData[option.field] ? 'bg-green-600' : 'bg-gray-200'
                      )}
                      role="switch"
                      aria-checked={formData[option.field]}
                    >
                      <span
                        className={cn(
                          'pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                          formData[option.field] ? 'translate-x-5' : 'translate-x-0'
                        )}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 pr-16">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
            {success}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={saving}
          className={cn(
            'w-full py-4 rounded-xl font-medium text-lg transition-colors',
            saving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
          )}
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              Saving...
            </span>
          ) : (
            'Save Settings'
          )}
        </button>
      </form>
    </PortalLayout>
  );
};

export default CommunicationSettings;
